/**
 * Slack — floating bubble + quick-post panel state.
 *
 * Mirrors AircallContext's expanded/collapsed pattern so the two docks
 * behave consistently. Unlike Aircall, there's no embedded SDK iframe —
 * we just POST to /api/slack/post via the existing apiClient. That means
 * we can mount/unmount the panel freely without losing any session.
 *
 * Channels are lazy-loaded the first time the panel opens (so the workspace
 * fetch doesn't slow down initial portal load) and cached in memory.
 */
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getSlackStatus, listSlackChannels } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SlackContext = createContext(null);

export const useSlack = () => useContext(SlackContext);

export function SlackProvider({ children }) {
  const [expanded, setExpanded]   = useState(false);
  const [status, setStatus]       = useState(null);     // {ok, configured, team, default_channel,...}
  const [channels, setChannels]   = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelsError, setChannelsError]     = useState(null);
  const [draft, setDraft]         = useState("");   // prefill for the composer
  const { apiReady } = useAuth();

  // One-time status probe — lets the collapsed pill show a green dot when the
  // bot is online, just like Aircall.
  //
  // Waits for `apiReady` rather than running on mount. `/api/slack/status` is an
  // authenticated route, and this provider sits below AuthProvider, so its
  // effects run *first*: probing on mount meant a guaranteed 401 on every page
  // load, which the response interceptor then read as the user's session having
  // been rejected.
  useEffect(() => {
    if (!apiReady) return;
    (async () => {
      try {
        const s = await getSlackStatus();
        setStatus(s);
      } catch (e) {
        setStatus({ ok: false, configured: false, error: e?.message });
      }
    })();
  }, [apiReady]);

  // Lazy-load channels the first time the panel opens. Subsequent opens
  // reuse the cached list; the small "↻" button in the dock forces a refresh.
  const loadChannels = useCallback(async (force = false) => {
    if (!force && channels.length > 0) return;
    setLoadingChannels(true);
    setChannelsError(null);
    try {
      const r = await listSlackChannels(true);
      setChannels(r.channels || []);
    } catch (e) {
      setChannelsError(e?.response?.data?.detail || e?.message || "Failed to load channels");
    } finally {
      setLoadingChannels(false);
    }
  }, [channels.length]);

  const expand = useCallback(() => {
    setExpanded(true);
    // Fire-and-forget — the dropdown shows a loading state until ready.
    loadChannels(false);
  }, [loadChannels]);

  const collapse = useCallback(() => setExpanded(false), []);

  // Open the dock with a prefilled message (e.g. an email snap from the
  // Applications Inbox). The dock consumes the draft and clears it.
  const expandWithDraft = useCallback((text) => {
    setDraft(text || "");
    setExpanded(true);
    loadChannels(false);
  }, [loadChannels]);

  const clearDraft = useCallback(() => setDraft(""), []);

  return (
    <SlackContext.Provider value={{
      expanded, expand, collapse,
      status,
      channels, loadingChannels, channelsError, reloadChannels: () => loadChannels(true),
      draft, expandWithDraft, clearDraft,
    }}>
      {children}
    </SlackContext.Provider>
  );
}
